import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { ClickAway } from '@components/core-ui/ClickAway';
import { cn } from '@helpers/cn';
import { useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { getInitialPlacement } from './helpers/getInitialPlacement';
import { getPopoverAvailableHeight } from './helpers/getPopoverAvailableHeight';
import { TProps } from './types/TProps';

const VIEWPORT_PADDING = 8;

export const PopoverFloating = ({
  children,
  onClose,
  open,
  anchorEl,
  placement = 'bottom-end',
  offset: offsetDistance = 6,
  maxHeight,
  className,
}: TProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const panel = panelRef.current;

    if (!open || !anchorEl || !panel) {
      return;
    }

    const restoreRequestedHeight = () => {
      panel.style.maxHeight = maxHeight === undefined ? '' : `${maxHeight}px`;
    };

    restoreRequestedHeight();

    const anchorRect = anchorEl.getBoundingClientRect();
    const initialPlacement = getInitialPlacement({
      preferredPlacement: placement,
      anchorTop: anchorRect.top,
      anchorBottom: anchorRect.bottom,
      viewportHeight: window.innerHeight,
      desiredHeight: Math.min(panel.scrollHeight, maxHeight ?? Number.POSITIVE_INFINITY),
      offset: offsetDistance,
      padding: VIEWPORT_PADDING,
    });

    return autoUpdate(anchorEl, panel, () => {
      restoreRequestedHeight();

      computePosition(anchorEl, panel, {
        placement: initialPlacement,
        strategy: 'fixed',
        middleware: [
          offset(offsetDistance),
          flip({ padding: VIEWPORT_PADDING }),
          shift({ padding: VIEWPORT_PADDING }),
          size({
            padding: VIEWPORT_PADDING,
            apply: ({ availableHeight, elements }) => {
              elements.floating.style.maxHeight = `${getPopoverAvailableHeight({ availableHeight, maxHeight })}px`;
            },
          }),
        ],
      }).then(({ x, y }) => {
        panel.style.left = `${x}px`;
        panel.style.top = `${y}px`;
      });
    });
  }, [anchorEl, maxHeight, offsetDistance, open, placement]);

  if (!open || !anchorEl) {
    return null;
  }

  return ReactDOM.createPortal(
    <ClickAway ignoreElement={anchorEl} onClickAway={onClose}>
      <div
        ref={panelRef}
        className={cn(
          'fixed left-0 top-0 z-10000 flex h-max w-max max-w-[calc(100vw-1rem)] flex-col overflow-auto rounded-md',
          'border border-border bg-surface text-foreground shadow-popover',
          className,
        )}
      >
        {children}
      </div>
    </ClickAway>,
    document.body,
  );
};
